import React, { createContext, useContext, useEffect, useState } from 'react';
import adminApi, { clearSession, getStoredUser, getToken, setSession } from '../lib/adminApi';

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser());
  const [checking, setChecking] = useState(Boolean(getToken()));

  // On mount, if we have a token, verify it with /me; this also catches
  // tokens that were revoked or expired in another tab.
  useEffect(() => {
    let mounted = true;
    if (!getToken()) {
      setChecking(false);
      return;
    }
    adminApi
      .get('/me')
      .then(({ data }) => {
        if (!mounted) return;
        setUser(data);
        setSession(data, getToken());
      })
      .catch(() => {
        if (!mounted) return;
        clearSession();
        setUser(null);
      })
      .finally(() => mounted && setChecking(false));
    return () => {
      mounted = false;
    };
  }, []);

  const login = async (email, password) => {
    const { data } = await adminApi.post('/login', { email, password });
    setSession({ id: data.id, email: data.email, role: data.role, name: data.name }, data.token);
    setUser({ id: data.id, email: data.email, role: data.role, name: data.name });
    return data;
  };

  const logout = async () => {
    try {
      await adminApi.post('/logout');
    } catch {
      /* ignore network errors on logout */
    }
    clearSession();
    setUser(null);
  };

  return (
    <AdminAuthContext.Provider value={{ user, checking, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used inside AdminAuthProvider');
  return ctx;
};
